import { generatePath, matchPath } from "react-router";
import { filter, values, includes, replace, isEmpty, last } from "lodash";
import { getGroupType } from "src/util/groupType";

const URL = {
  NEW_USER_LANDING: "/start",
  INSIGHTS: "/:workspaceId?/insights",
  SUMMARY_LEGACY: "/:workspaceId?/summary",
  SEARCH: "/:workspaceId?/search/:type/:searchTerm?",
  PEOPLE: "/:workspaceId?/people",
  TAG_EXPLORE: "/:workspaceId?/explore/tags/:id",
  TEAM_EXPLORE: "/:workspaceId?/explore/teams/:id",
  GOALS_EXPLORE: "/:workspaceId?/explore/goals/:id?",
  EXPLORE: "/:workspaceId?/explore",
  USER_HOME_BASE_PATH: "/:workspaceId?/user",
  USER_HOME: "/:workspaceId?/user/:tabType",
  TEAMS: "/:workspaceId?/teams",
  TEAM_DETAILS_BASE_PATH: "/:workspaceId?/teams/:id",
  TEAM_DETAILS: "/:workspaceId?/teams/:id/:tabType",
  PERSON_DETAILS: "/:workspaceId?/people/:id/:tabType",
  PERSON_DETAILS_EMAIL_LINK: "/:workspaceId?/people-link/:email",
  PERSON_DETAILS_BASE_PATH: "/:workspaceId?/people/:id",
  PERSON_DETAILS_FROM_DIRECTORY: "/:workspaceId?/people/:id",
  WORKFORCE_PLANNING_DASHBOARD: "/:workspaceId?/workforce-planning-dashboard",
  DATASOURCE: "/:workspaceId?/configuration/datasource/:dataSourceId",
  WORKSPACES: "/:workspaceId?/workspaces",
  WORKSPACE_SETTINGS_ROOT: "/:workspaceId?/workspace-settings",
  WORKSPACE_SETTINGS: "/:workspaceId?/workspace-settings/:tabType",
  WORKSPACE_CONFIGURATION:
    "/:workspaceId?/configuration/:tabType/:actionType?/:integrationType?",
  CREATE_WORKSPACE: "/workspaces/create",
  LOGOUT: "/logout",
  // Routes from paths.js that were missing

  VISUALISATION_WITH_ID: "/:workspaceId?/explore/:pageType/:aggregateId",
  INTERNAL_DASHBOARD_GENERAL: "/:workspaceId?/internal-dashboard/general",
  INTERNAL_DASHBOARD: "/:workspaceId?/internal-dashboard/:tabType",
  USER_REPORT: "/:workspaceId?/user-report",
  REPORTS: process.env.REACT_APP_REPORTS_DOMAIN,
  DIRECTORY_USER_HOME_BASE_PATH: "/:workspaceId?/home",
  DIRECTORY_USER_HOME: "/:workspaceId?/home/:tabType",
  ADMIN: "/:workspaceId?/admin/dashboard",
  ADMIN_DASHBOARD: "/:workspaceId?/admin/dashboard/:tabType",
  TAG_DETAILS: "/:workspaceId?/tags/:id",
  API_DOCS: "/:workspaceId?/api-docs",
  LOGIN_HISTORY_PAGE: "/:workspaceId?/login-history",
  OBJECTIVES: "/:workspaceId?/objectives",
  // Workflow routes
  WORKFLOWS: "/:workspaceId?/workflows",
  WORKFLOW_BUILDER: "/:workspaceId?/workflows/:id",
  WORKFLOW_RUNS: "/:workspaceId?/workflows/:id/runs",
  WORKFLOW_RUN_DETAILS: "/:workspaceId?/workflows/:workflowId/runs/:runId",
  AI_DASHBOARDS: "/:workspaceId?/dashboards/:chatId/:dashboardId",
  DESIGNS: "/designs",
  DESIGN_DETAIL: "/designs/:id",
  DESIGN_EDIT: "/designs/:id/edit",
  // NOTE: This must always be the last path in the URL to avoid conflicts with other paths.
  APP_HOME: "/:workspaceId?",
};

// Exclude paths that should not be redirected to fallback
const excludedPaths = [URL.DATASOURCE];

// any urls that dont require authentication
export const PUBLIC_URLS = [URL.LOGOUT];

export const TEAM_TAB = {
  DETAILS: "details",
  ORG_CHART: "orgchart",
  INSIGHTS: "insights",
  MEMBERSHIPS: "memberships",
  STRATEGY: "strategy",
  ASSOCIATIONS: "associations",
  PRINCIPLES: "principles",
  PLANNER: "planner", // Deprecated. TODO: Temporarily added as per customer request. Should remove after 3-4 months.
  REQUEST_ALLOCATION_NUMBERS: "request-allocations",
  APPROVE_ALLOCATION_NUMBERS: "approve-allocations",
  INDIVIDUAL_ALLOCATIONS: "individual-allocations",
  INDIVIDUAL_ALLOCATIONS_PERSON: "individual-allocations-person",
  REQUEST_ALLOCATION_PEOPLE: "assign-to-teams",
  APPROVE_ALLOCATION_PEOPLE: "assign-to-groups",
  FORECAST: "forecast",
  HISTORY: "history",
  METRICS: "metrics",
  CHANGELOG: "changelog",
};

export const PERSON_TAB = {
  DETAILS: "details",
  ALLOCATIONS: "allocations",
  USER_DASHBOARD: "dashboard",
  HISTORY: "history",
  LINE_MANAGEMENT: "line-management",
};

export const pathList = values(URL);

export const matchPaths = (pathUrl, pathListToMatch = pathList) => {
  const pathname = pathUrl || window.location.pathname;

  if (Array.isArray(pathListToMatch)) {
    for (const path of pathListToMatch) {
      const match = matchPath(pathname, {
        path,
        exact: true,
        strict: true,
      });
      if (match) {
        return match;
      }
    }
    return null;
  }

  return matchPath(pathname, {
    path: pathListToMatch,
    exact: true,
    strict: true,
  });
};

export const workspaceIdFromUrl = () => matchPaths()?.params?.workspaceId;

export const url = (path, params) => {
  const workspaceId = params?.workspaceId ?? workspaceIdFromUrl();
  return generatePath(path, { ...params, workspaceId });
};

const isPathExcludedFromFallback = (value) => {
  return includes(excludedPaths, value);
};

export const fallbackPath = (pathUrl) => {
  const currentPath = pathUrl || window.location.pathname;
  const pathListWithIdParam = filter(
    values(URL),
    (value) => includes(value, ":id") && !isPathExcludedFromFallback(value)
  );
  const matchedPath = matchPaths(currentPath, pathListWithIdParam);
  if (matchedPath) {
    const fallback = replace(matchedPath.path, /\/:id.*/, "");
    return url(fallback);
  }
  return currentPath;
};

// Helper functions moved from paths.js
const getDecodedValue = (value) => {
  return value ? decodeURI(value) : null;
};

export const getSearchTerm = () =>
  decodeURI(matchPaths()?.params?.searchTerm || "");

export const getSearchPath = (type, searchTerm, useFallbackFromUrl = true) => {
  const urlParams = matchPaths()?.params;
  return generatePath(URL.SEARCH, {
    type: type || urlParams?.type || "all",
    searchTerm:
      searchTerm ||
      (useFallbackFromUrl ? getDecodedValue(urlParams?.searchTerm) : null),
    workspaceId: urlParams?.workspaceId,
  });
};

export const workspaceRootUrl = (workspaceId) =>
  generatePath(URL.APP_HOME, { workspaceId });

export const navigateToWorkspace = (workspaceId) => {
  window.location = workspaceRootUrl(workspaceId);
};

export const onNavigateUp = (group, history, groupTypes) => {
  const { parentIds } = group;
  const groupType = getGroupType(group, groupTypes);
  if (groupType.isRoot || isEmpty(parentIds)) {
    history.push(url(URL.EXPLORE));
  } else {
    history.push(url(URL.TEAM_DETAILS_BASE_PATH, { id: last(parentIds) }));
  }
};

export default URL;
